<template>
  <StepCard
    step-id="create_a_smart_notification"
    :description="t('description')"
    :edit-label="t('edit')"
    :completed
    :errors="serverErrors"
    :saving
    :save-label="t('save')"
    :save-hint
    @save="save"
    @skip="$emit('skip')"
    @cancel="$emit('cancel')"
  >
    <template #summary>
      <div class="flex flex-col gap-3">
        <div
          v-for="template in createdTemplates"
          :key="template.key"
          class="flex items-center justify-between gap-4 rounded-lg border border-gray-200 bg-white p-4"
          :data-testid="`notification-summary-${template.key}`"
        >
          <div class="min-w-0">
            <p class="text-sm font-medium text-gray-900">{{ t(`templates.${template.key}.title`) }}</p>
            <p class="text-xs text-gray-500">{{ t(`templates.${template.key}.description`) }}</p>
            <p class="mt-1 text-xs text-gray-500">{{ t(`templates.${template.key}.frequency`) }} · {{ t('email') }}</p>
          </div>
          <span class="inline-flex shrink-0 select-none items-center gap-1 whitespace-nowrap rounded-full bg-green-100 px-2 py-1 text-xs font-regular text-green-800">
            <ZoneIcon
              icon="check_circle"
              class="text-sm!"
              aria-hidden="true"
            />
            {{ t('created') }}
          </span>
        </div>
      </div>
    </template>

    <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
      <div
        v-for="template in templates"
        :key="template.key"
        class="flex flex-col gap-3 rounded-lg border p-4 transition-colors"
        :class="template.on ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white'"
        :data-testid="`notification-template-${template.key}`"
      >
        <div class="flex items-center justify-between gap-3">
          <p class="min-w-0 flex-1 text-sm font-semibold text-gray-900">{{ t(`templates.${template.key}.title`) }}</p>
          <ZoneButton
            size="sm"
            variant="outline"
            :color="template.on ? 'gray_alt' : 'primary'"
            :trailing-icon="template.on ? 'check' : null"
            :data-testid="`notification-create-${template.key}`"
            @click="toggle(template)"
          >
            {{ template.on ? t('created') : t('create') }}
          </ZoneButton>
        </div>
        <div class="flex flex-wrap items-center gap-3">
          <ZonePill icon="schedule">{{ t(`templates.${template.key}.frequency`) }}</ZonePill>
          <ZonePill icon="notifications">{{ t('email') }}</ZonePill>
        </div>
        <p class="line-clamp-2 text-sm text-gray-500">{{ t(`templates.${template.key}.description`) }}</p>
      </div>
    </div>

    <template #dialog>
      <ZoneModal
        v-model="trialDialogOpen"
        width="60rem"
        data-testid="insights-trial-dialog"
      >
        <template #title>
          <span class="insights-trial-title block pl-12 text-center text-2xl">{{ t('trial_title') }}</span>
        </template>
        <p class="text-center text-md font-semibold text-gray-900">{{ t('trial_description') }}</p>
        <img
          :src="GETTING_STARTED_FREE_TRIAL_INSIGHTS"
          :alt="t('trial_image')"
          class="mx-auto w-44"
        />
        <p class="text-center text-sm text-gray-600">{{ t('trial_price', { price: data.insights_trial_monthly_price }) }}</p>
        <template #footer="{ close }">
          <ZoneButton
            variant="outline"
            color="gray_alt"
            class="mx-auto"
            data-testid="insights-trial-close"
            @click="close"
          >
            {{ t('close') }}
          </ZoneButton>
        </template>
      </ZoneModal>
    </template>
  </StepCard>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import StepCard from '~/components/experiments/0239_getting_started_checklist/StepCard.vue';
import { useStepSave } from '~/components/experiments/0239_getting_started_checklist/useStepSave';
import { SmartNotificationsSettingsResource } from '~/resources';
import { buildNotificationParams } from '~/utils/smartNotificationPresets';
import ZoneButton from 'zone/components/base/Button.vue';
import ZoneIcon from 'zone/components/base/Icon.vue';
import ZoneModal from 'zone/components/base/Modal.vue';
import ZonePill from 'zone/components/base/Pill.vue';
import { SETTINGS_SMART_NOTIFICATIONS } from '~/app_constants';
import { GETTING_STARTED_FREE_TRIAL_INSIGHTS } from '~/rails_assets';

const PRESET_KEYS = ['high_activity', 'overworking', 'underworking', 'low_activity'];

const { data, completed, organization } = defineProps({
  data: { type: Object, required: true },
  completed: { type: Boolean },
  organization: { type: Object, required: true },
});

const emit = defineEmits(['saved', 'skip', 'cancel', 'refresh']);

const { t } = useI18n();
const { saving, serverErrors, failures, finishStep, run } = useStepSave(emit);

const wanted = ref({});
const trialDialogClosed = ref(false);
const trialDialogOpen = computed({
  get: () => data.insights_trial_started && !trialDialogClosed.value,
  set: (open) => { trialDialogClosed.value = !open; },
});

const templates = computed(() => PRESET_KEYS.map((key) => {
  const id = data.notifications.find((notification) => notification.name === SETTINGS_SMART_NOTIFICATIONS.presets[key].name)?.id;

  return { key, id, on: wanted.value[key] ?? !!id };
}));

const save = () => run(async () => {
  const changed = templates.value.filter((template) => template.on !== !!template.id);
  const results = await Promise.allSettled(changed.map(({ key, id }) => (id
    ? SmartNotificationsSettingsResource.delete({ organizationId: organization.id, smartNotificationId: id })
    : SmartNotificationsSettingsResource.create(
      { organizationId: organization.id },
      { ...buildNotificationParams(key), source: 'getting_started_flow' },
    ))));

  emit('refresh');

  const messages = failures(results);

  return messages.length ? messages : finishStep(organization.id, 'create_a_smart_notification');
});
const saveHint = computed(() => (templates.value.some((template) => template.on) ? null : t('save_hint')));
const createdTemplates = computed(() => templates.value.filter((template) => template.id));

const toggle = ({ key, on }) => {
  wanted.value = { ...wanted.value, [key]: !on };
};
</script>

<style>
@keyframes insights-trial-fade { from { opacity: 0; } }
@keyframes insights-trial-zoom { from { scale: 0.96; } }

[data-testid="modal-overlay"]:has(.insights-trial-title) { animation: insights-trial-fade 150ms ease-out; }
[data-testid="modal-overlay"]:has(.insights-trial-title) [role="dialog"] { animation: insights-trial-zoom 200ms ease-out; }

@media (prefers-reduced-motion: reduce) {
  [data-testid="modal-overlay"]:has(.insights-trial-title),
  [data-testid="modal-overlay"]:has(.insights-trial-title) [role="dialog"] { animation: none; }
}
</style>

<i18n lang="json" src="~/components/experiments/0239_getting_started_checklist/steps.json"></i18n>

<i18n lang="json">
{
  "en": {
    "description": "Get notified automatically when specific events happen in your organization.",
    "templates": {
      "high_activity": { "title": "Suspicious activity level", "description": "Flags activity patterns that look automated rather than human.", "frequency": "Hourly" },
      "overworking": { "title": "Members overworking", "description": "Someone is tracking well beyond their agreed limit.", "frequency": "Weekly" },
      "underworking": { "title": "Members underworking", "description": "Someone is tracking far below their schedule.", "frequency": "Weekly" },
      "low_activity": { "title": "Members with low activity level", "description": "Activity drops below the threshold you set.", "frequency": "Weekly" }
    },
    "email": "Email",
    "create": "Create",
    "created": "Created",
    "save": "Save smart notifications",
    "save_hint": "Pick at least one notification first.",
    "edit": "Edit smart notifications",
    "trial_title": "Free Insights Trial",
    "trial_description": "Congrats! You got a free 14 day trial of our most popular add-on: Insights.",
    "trial_image": "Free trial insights",
    "trial_price": "You can cancel this at any time during the 14 days. After that you will be charged ${price}/seat/month.",
    "close": "Close"
  }
}
</i18n>
